import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { inject, observer } from 'mobx-react';

const MAX_LENGTH = 4000;

/**
 * In-house support form (replaces the third-party chat widget). Submits to
 * the API, which persists the message and forwards it to Slack, and shows
 * the user's own history underneath.
 */
const SupportModal = (props) => {
  const { open, onClose } = props;
  const { supportMessagesStore, accountStore } = props.store;

  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setMessage('');
      setSent(false);
      setError(null);
      supportMessagesStore.loadMyMessages();
    }
  }, [open]);

  const handleSend = async () => {
    setError(null);
    if (!message.trim()) {
      setError('Please write a message first.');
      return;
    }
    try {
      await supportMessagesStore.sendMessage(message.trim());
      setSent(true);
      setMessage('');
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Could not send your message. Please try again.');
    }
  };

  const phone = accountStore.user?.phone;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Contact support</DialogTitle>
      <DialogContent>
        {sent && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Message sent — our support team has it and will get back to you
            {phone ? ' by phone or SMS' : ''}.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!phone && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have no phone number on your account, so support can only reply by email.
            Adding one lets them call or text you back.
          </Alert>
        )}

        <TextField
          label="How can we help?"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value.slice(0, MAX_LENGTH))
            if (error) setError(null)
          }}
          multiline
          minRows={4}
          fullWidth
          margin="normal"
          helperText={`${message.length}/${MAX_LENGTH}`}
        />

        {supportMessagesStore.myMessages.length > 0 && (
          <Box mt={2}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Your previous messages
            </Typography>
            {supportMessagesStore.myMessages.slice(0, 10).map((m) => (
              <Box key={m.id} sx={{ mb: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(m.createdAt).toLocaleString()}
                  </Typography>
                  <Chip
                    size="small"
                    label={m.status === 'HANDLED' ? 'Handled' : 'New'}
                    color={m.status === 'HANDLED' ? 'success' : 'default'}
                  />
                </Box>
                <Typography variant="body2" color="text.primary">{m.message}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <LoadingButton
          variant="contained"
          loading={supportMessagesStore.sending}
          onClick={handleSend}
        >
          Send message
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default inject('store')(observer(SupportModal));
